import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { SessionReferralGuideService } from '../../../../../lazy-modules/session-details/session-referral-guide/session-referral-guide.service';
import { EGuideContext } from '@project/view-models';
import { TranslateService } from '@project/translate';
import { getMedicalReferralTypeLabel } from '../../../../view-models/consts/referral-guide';

interface ReferralGuide {
  id: string;
  type: string;
  created_at: string;
}

@Component({
  selector: 'app-referral-guide',
  templateUrl: './referral-guide.component.html',
  styleUrls: ['./referral-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralGuideComponent implements OnInit, OnDestroy {
  @Input() logId!: string;
  @Input() context!: EGuideContext;

  public guides: ReferralGuide[] = [];
  public isLoading = false;
  public error = false;
  public resolveLabel = getMedicalReferralTypeLabel;
  public currentPage = 1;
  public readonly ITEMS_PER_PAGE = 10;
  public totalItems = 0;
  public totalPages = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private referralGuideService: SessionReferralGuideService,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadGuides(1);
  }

  loadGuides(page: number): void {
    this.isLoading = true;
    this.error = false;

    const request$ =
      this.context === EGuideContext.PATIENT
        ? this.referralGuideService.getReferralGuidesviaPatientId(this.logId, page, this.ITEMS_PER_PAGE)
        : this.referralGuideService.getReferralGuidesviaRequisitionId(this.logId, page, this.ITEMS_PER_PAGE);

    request$
      .pipe(
        tap((res) => {
          this.totalItems = res.totalItemsCount;
          this.totalPages = res.requestTotalPages;
          this.currentPage = page;

          this.guides = (res.items ?? []).map((item) => ({
            id: item.id,
            type: item.type,
            created_at: item.created_at,
          }));

          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        catchError(() => {
          this.error = true;
          this.guides = [];
          this.isLoading = false;
          this.notificationsService.error({
            message: TranslateService.localize('errors.exams-referral-guides'),
          });
          this.cdr.markForCheck();
          return of();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage) {
      this.loadGuides(page);
    }
  }

  onGuideDownloadClick(guide: ReferralGuide): void {
    this.referralGuideService
      .downloadGuide(guide.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (url) => url && window.open(url, '_blank'),
        error: () =>
          this.notificationsService.error({
            message: TranslateService.localize('errors.exams-referral-guides'),
          }),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
